/**
 * Phase 3 — Astronomy & Planet Intelligence UI cards.
 *
 * Renders three blocks on the Client Kundli page:
 *   • Sun Intelligence
 *   • Moon Intelligence
 *   • Premium Planet Summary (per-planet astronomy + classical status)
 *
 * Backend payload contract:
 *   data.sun_intelligence     — object
 *   data.moon_intelligence    — object
 *   data.planet_premium       — { Sun..Saturn, Rahu, Ketu }
 *
 * Pure presentational. No logic, no LLM.
 */
import React, { memo, useState } from 'react';
import { Sun, Moon, Sparkles, Info, Copy, Check, Download } from 'lucide-react';

const PLANETS_ORDER = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

/* ── shared atoms ───────────────────────────────────────────────── */
const CopyButton = ({ payload, label = 'Copy JSON', testid }) => {
  const [state, setState] = useState('idle'); // idle | done | err
  const onCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setState('done');
    } catch (e) {
      setState('err');
    }
    setTimeout(() => setState('idle'), 1600);
  };
  const txt = state === 'done' ? 'कॉपी हुआ!' : state === 'err' ? 'कॉपी नहीं हुआ' : label;
  return (
    <button
      onClick={onCopy}
      data-testid={testid}
      aria-label={label}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
        state === 'err'
          ? 'bg-rose-500/15 text-rose-300 border-rose-400/40'
          : state === 'done'
          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40'
          : 'bg-white/5 border-white/10 hover:bg-gold/15 hover:border-gold/40 hover:text-gold text-text-muted'
      }`}
    >
      {state === 'done' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {txt}
    </button>
  );
};

const DownloadButton = ({ payload, filename, testid }) => {
  const onDownload = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <button
      onClick={onDownload}
      data-testid={testid}
      aria-label={`Download ${filename}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 hover:bg-gold/15 hover:border-gold/40 hover:text-gold text-text-muted transition-colors"
    >
      <Download className="w-3 h-3" /> JSON
    </button>
  );
};

const Card = ({ id, icon: Icon, title, testid, accent = 'gold', actions, children }) => {
  const accentMap = {
    gold: 'border-gold/20 from-purple-950/30 to-purple-950/30',
    sun: 'border-orange-400/25 from-orange-900/15 to-purple-950/30',
    moon: 'border-blue-400/25 from-blue-900/15 to-purple-950/30',
  };
  const iconMap = {
    gold: 'bg-gold/15 border-gold/30 text-gold',
    sun: 'bg-orange-500/15 border-orange-400/30 text-orange-200',
    moon: 'bg-blue-500/15 border-blue-400/30 text-blue-200',
  };
  const titleColor = accent === 'sun' ? 'text-orange-200' : accent === 'moon' ? 'text-blue-200' : 'text-gold';
  return (
    <section
      id={id}
      data-testid={testid}
      className={`rounded-2xl border bg-gradient-to-br ${accentMap[accent]} backdrop-blur-sm p-6 shadow-xl`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${iconMap[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className={`font-heading text-xl ${titleColor}`}>{title}</h2>
        {actions && <div className="ml-auto flex gap-1.5">{actions}</div>}
      </div>
      {children}
    </section>
  );
};

const Stat = ({ label, value, tooltip, mono = false, highlight = false }) => (
  <div
    className={`rounded-xl border px-3 py-2.5 ${
      highlight ? 'bg-gold/8 border-gold/30' : 'bg-white/[0.03] border-white/8'
    } group relative`}
    data-testid={`stat-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-muted">
      {label}
      {tooltip && (
        <span className="relative">
          <Info className="w-3 h-3 inline-block opacity-60 group-hover:opacity-100 cursor-help" />
          <span
            className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-50 w-56 p-2 rounded-lg bg-black/95 border border-gold/30 text-[11px] text-text-main shadow-xl normal-case tracking-normal pointer-events-none"
            role="tooltip"
          >
            {tooltip}
          </span>
        </span>
      )}
    </div>
    <div className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>
      {value ?? '—'}
    </div>
  </div>
);

const Bar = ({ pct, accent = 'gold' }) => {
  const colorMap = {
    gold: 'from-gold/30 to-gold',
    sun: 'from-orange-400/40 to-orange-300',
    moon: 'from-blue-400/40 to-blue-300',
  };
  const safe = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden border border-white/10">
      <div
        className={`h-full bg-gradient-to-r ${colorMap[accent]} transition-all duration-700`}
        style={{ width: `${safe}%` }}
      />
    </div>
  );
};

/* ── Sun Intelligence ───────────────────────────────────────────── */
export const SunIntelligenceCard = memo(function SunIntelligenceCard({ sun }) {
  if (!sun) return null;
  return (
    <Card
      id="sun-intelligence"
      icon={Sun}
      title="सूर्य बुद्धिमत्ता · Sun Intelligence"
      testid="card-sun-intelligence"
      accent="sun"
      actions={
        <>
          <CopyButton payload={sun} label="कॉपी" testid="sun-copy-btn" />
          <DownloadButton payload={sun} filename="sun_intelligence.json" testid="sun-download-btn" />
        </>
      }
    >
      <div className="text-center mb-5">
        <p className="text-[11px] uppercase tracking-[0.22em] text-orange-300/70">{sun.sign_en}</p>
        <p className="font-heading text-4xl text-orange-200 mt-1">{sun.sign_hi}</p>
        <p className="text-text-muted text-sm mt-1">
          {sun.degree_in_sign?.toFixed(2)}° · {sun.motion_state_hi}
        </p>
      </div>

      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-wider text-orange-300/80">प्रकाशन · Illumination</span>
          <span className="font-mono text-sm text-orange-200">{sun.illumination_pct}%</span>
        </div>
        <Bar pct={sun.illumination_pct} accent="sun" />
        <p className="text-[11px] text-text-muted mt-1.5">{sun.phase_name_hi}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        <Stat
          label="दैनिक गति"
          value={`${sun.speed_deg_per_day?.toFixed(4)}°`}
          tooltip="सूर्य की दैनिक गति · Sun's daily motion in degrees (Cheshta-bala basis). मध्य गति ≈ 0.9856°/दिन"
          mono
        />
        <Stat
          label="मध्य गति"
          value={`${sun.mean_speed_deg_per_day}°`}
          tooltip="शास्त्रोक्त मध्य गति · Classical mean daily motion"
          mono
        />
        <Stat
          label="क्रांति · Declination"
          value={sun.declination}
          tooltip="आकाशीय भूमध्य से सूर्य का कोणीय अंतर · Sun's angular distance from celestial equator (max ±23.45°). उत्तर ⇒ उत्तरायण; दक्षिण ⇒ दक्षिणायन"
          mono
        />
        <Stat
          label="R.A. · विषुवांश"
          value={sun.right_ascension}
          tooltip="Right Ascension — आकाशीय याम्योत्तर से सूर्य का कोणीय अंतर (15° = 1 घंटा)"
          mono
        />
        <Stat
          label="अयन · Ayan"
          value={sun.ayan_hi}
          tooltip="जन्म तिथि पर सूर्य की क्रांति-दिशा से निर्धारित"
        />
        <Stat
          label="कांति परिमाण"
          value={sun.apparent_magnitude}
          tooltip="Apparent magnitude — खुली आँख से दिखाई देने वाली कांति"
          mono
        />
      </div>

      <p className="text-[10px] text-text-muted/60 mt-4">स्रोत: {(sun.shastra_refs || []).join(' · ')}</p>
    </Card>
  );
});

/* ── Moon Intelligence ──────────────────────────────────────────── */
export const MoonIntelligenceCard = memo(function MoonIntelligenceCard({ moon }) {
  if (!moon) return null;
  return (
    <Card
      id="moon-intelligence"
      icon={Moon}
      title="चंद्र बुद्धिमत्ता · Moon Intelligence"
      testid="card-moon-intelligence"
      accent="moon"
      actions={
        <>
          <CopyButton payload={moon} label="कॉपी" testid="moon-copy-btn" />
          <DownloadButton payload={moon} filename="moon_intelligence.json" testid="moon-download-btn" />
        </>
      }
    >
      <div className="text-center mb-5">
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-300/70">{moon.sign_en}</p>
        <p className="font-heading text-4xl text-blue-200 mt-1">{moon.sign_hi}</p>
        <p className="text-text-muted text-sm mt-1">
          {moon.degree_in_sign?.toFixed(2)}° · {moon.motion_state_hi} · {moon.paksha_hi}
        </p>
      </div>

      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-wider text-blue-300/80">चंद्र कला · Illumination</span>
          <span className="font-mono text-sm text-blue-200">{moon.illumination_pct}%</span>
        </div>
        <Bar pct={moon.illumination_pct} accent="moon" />
        <p className="text-[11px] text-text-muted mt-1.5">
          {moon.phase_name_hi} · तिथि #{moon.tithi_num}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        <Stat
          label="दैनिक गति"
          value={`${moon.speed_deg_per_day?.toFixed(4)}°`}
          tooltip="चंद्र की दैनिक गति · Moon's daily motion (mean ≈ 13.18°/दिन). शीघ्र = कर्मठ; मंद = स्थिर मानस"
          mono
        />
        <Stat
          label="मध्य गति"
          value={`${moon.mean_speed_deg_per_day}°`}
          mono
        />
        <Stat
          label="क्रांति · Declination"
          value={moon.declination}
          tooltip="चंद्र की क्रांति आकाशीय भूमध्य से ±28.5° तक हो सकती है (Lunar Nodal Cycle 18.6 years)"
          mono
        />
        <Stat
          label="R.A. · विषुवांश"
          value={moon.right_ascension}
          tooltip="Right Ascension — आकाशीय याम्योत्तर से चंद्र का कोणीय अंतर"
          mono
        />
        <Stat
          label="सूर्य से कोणीय अंतर"
          value={`${moon.elongation_from_sun_deg?.toFixed(2)}°`}
          tooltip="Sun-Moon elongation — 0° = अमावस्या · 180° = पूर्णिमा. हर 12° पर एक तिथि"
          mono
          highlight
        />
        <Stat
          label="पथ अक्षांश"
          value={`${moon.ecliptic_latitude_deg?.toFixed(2)}°`}
          tooltip="Ecliptic latitude — क्रांतिवृत्त से चंद्र का विचलन (≤ ±5°). शून्य ⇒ ग्रहण योग संभव"
          mono
        />
        <Stat
          label="कांति परिमाण"
          value={moon.apparent_magnitude}
          tooltip="Apparent magnitude (पूर्णिमा ≈ −12.7, अमावस्या ≈ −1)"
          mono
        />
        <Stat
          label="व्यास (arcsec)"
          value={moon.apparent_diameter_arcsec}
          tooltip="दृश्य कोणीय व्यास · Apparent diameter ~1800 arcsec ≈ 30 arc-min"
          mono
        />
        <Stat
          label="पृथ्वी से दूरी"
          value={`${moon.distance_from_earth_km?.toLocaleString?.()} km`}
          tooltip="Distance from Earth — perigee ≈ 363,300 km; apogee ≈ 405,500 km"
          mono
        />
      </div>

      <p className="text-[10px] text-text-muted/60 mt-4">स्रोत: {(moon.shastra_refs || []).join(' · ')}</p>
    </Card>
  );
});

/* ── Premium Planet Summary ─────────────────────────────────────── */
const PILL_TONE_MAP = {
  good:    'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  bad:     'bg-rose-500/15 text-rose-300 border-rose-400/30',
  warn:    'bg-amber-500/15 text-amber-300 border-amber-400/30',
  cool:    'bg-cyan-500/15 text-cyan-300 border-cyan-400/30',
  regal:   'bg-gold/15 text-gold border-gold/30',
  neutral: 'bg-white/5 text-text-muted border-white/10',
};
const Pill = ({ children, tone = 'neutral' }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${PILL_TONE_MAP[tone]}`}>
    {children}
  </span>
);

export const PremiumPlanetSummaryCard = memo(function PremiumPlanetSummaryCard({ premium }) {
  if (!premium || Object.keys(premium).length === 0) return null;

  return (
    <Card
      id="planet-premium"
      icon={Sparkles}
      title="ग्रह विशेष सारणी · Premium Planet Summary"
      testid="card-planet-premium"
      actions={
        <>
          <CopyButton payload={premium} label="कॉपी" testid="premium-copy-btn" />
          <DownloadButton payload={premium} filename="planet_premium.json" testid="premium-download-btn" />
        </>
      }
    >
      <p className="text-xs text-text-muted mb-4">
        प्रत्येक ग्रह की पूर्ण खगोलीय एवं शास्त्रोक्त स्थिति · पंक्ति पर क्लिक करके विस्तार देखें ·
        स्रोत: BPHS Adhyaya 3 · Phaladeepika 2 · Sripati Paddhati
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gold/80 border-b border-gold/20">
            <tr>
              <th className="text-left py-2 pr-2 sticky left-0 bg-purple-950/50">ग्रह</th>
              <th className="text-left py-2 pr-2">राशि</th>
              <th className="text-left py-2 pr-2">गरिमा</th>
              <th className="text-left py-2 pr-2">गति स्थिति</th>
              <th className="text-right py-2 pr-2">दैनिक गति</th>
              <th className="text-right py-2 pr-2">क्रांति</th>
              <th className="text-right py-2 pr-2">सूर्य-दूरी</th>
              <th className="text-right py-2 pr-2">कला %</th>
              <th className="text-left py-2 pr-2">शास्त्रोक्त स्थिति</th>
            </tr>
          </thead>
          <tbody>
            {PLANETS_ORDER.map((nm) => {
              const p = premium[nm];
              if (!p) return null;
              const dignityTone = p.is_exact_exalted
                ? 'regal'
                : p.is_exalted
                ? 'good'
                : p.is_exact_debilitated
                ? 'bad'
                : p.is_debilitated
                ? 'bad'
                : p.is_mooltrikona
                ? 'regal'
                : p.is_own_sign
                ? 'good'
                : p.sign_lord_relation_en === 'friend'
                ? 'cool'
                : p.sign_lord_relation_en === 'enemy'
                ? 'warn'
                : 'neutral';
              return (
                <tr key={nm} className="border-b border-white/5 hover:bg-white/5" data-testid={`premium-row-${nm}`}>
                  <td className="py-2 pr-2 sticky left-0 bg-bg-primary/80 backdrop-blur">
                    <div className="font-semibold text-gold">{p.name_hi}</div>
                    <div className="text-[10px] text-text-muted">{p.name_en}</div>
                  </td>
                  <td className="py-2 pr-2">
                    <div>{p.sign_hi}</div>
                    <div className="text-[10px] text-text-muted">
                      {p.degree_in_sign?.toFixed(2)}° · स्वामी {p.sign_lord_hi}
                    </div>
                  </td>
                  <td className="py-2 pr-2"><Pill tone={dignityTone}>{p.dignity_hi}</Pill></td>
                  <td className="py-2 pr-2 text-text-muted">{p.motion_state_hi}</td>
                  <td className="py-2 pr-2 text-right font-mono text-xs">
                    {p.speed_deg_per_day?.toFixed(4)}°
                  </td>
                  <td className="py-2 pr-2 text-right font-mono text-xs">{p.declination}</td>
                  <td className="py-2 pr-2 text-right font-mono text-xs">
                    {p.distance_from_sun_deg != null ? `${p.distance_from_sun_deg.toFixed(1)}°` : '—'}
                  </td>
                  <td className="py-2 pr-2 text-right font-mono text-xs">
                    {p.illumination_pct != null ? `${p.illumination_pct.toFixed(0)}%` : '—'}
                  </td>
                  <td className="py-2 pr-2">
                    <div className="flex flex-wrap gap-1">
                      {p.is_retrograde && <Pill tone="warn">↺ वक्री</Pill>}
                      {p.is_combust && <Pill tone="bad">🔥 अस्त ({p.combust_orb_used_deg}°)</Pill>}
                      {p.is_exact_exalted && <Pill tone="regal">★ परमोच्च</Pill>}
                      {p.is_exact_debilitated && <Pill tone="bad">★ परमनीच</Pill>}
                      {p.is_mooltrikona && <Pill tone="regal">✦ मूलत्रिकोण</Pill>}
                      {p.is_stationary && <Pill tone="warn">⏸ स्तम्भित</Pill>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Naisargika Mitratva Reference Card */}
      <details className="mt-5 group">
        <summary className="cursor-pointer text-sm text-gold/80 hover:text-gold flex items-center gap-2" data-testid="toggle-naisargika-ref">
          <Info className="w-4 h-4" />
          नैसर्गिक मैत्री संदर्भ देखें · View Naisargika Friendship Reference (BPHS 3.56-58)
        </summary>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map((nm) => {
            const p = premium[nm];
            if (!p) return null;
            return (
              <div key={nm} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`naisargika-${nm}`}>
                <div className="font-heading text-base text-gold mb-2">{p.name_hi}</div>
                <div className="space-y-1.5 text-xs">
                  <div><span className="text-emerald-300/80">मित्र:</span> <span className="text-text-main">{p.naisargika.friends_hi.join(', ') || '—'}</span></div>
                  <div><span className="text-rose-300/80">शत्रु:</span> <span className="text-text-main">{p.naisargika.enemies_hi.join(', ') || '—'}</span></div>
                  <div><span className="text-text-muted">सम:</span> <span className="text-text-main">{p.naisargika.neutrals_hi.join(', ') || '—'}</span></div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-text-muted/80 mt-3 italic">
          ✦ राहु/केतु छाया ग्रह हैं — BPHS 3.56-58 में नैसर्गिक मैत्री केवल 7 ग्रहों के लिए परिभाषित है।
        </p>
      </details>
    </Card>
  );
});

export default function Phase3Section({ data }) {
  if (!data) return null;
  return (
    <>
      <SunIntelligenceCard sun={data.sun_intelligence} />
      <MoonIntelligenceCard moon={data.moon_intelligence} />
      <PremiumPlanetSummaryCard premium={data.planet_premium} />
    </>
  );
}
